/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Shared building blocks for every change renderer.
 *
 * Each entity renderer composes these rather than inventing its own layout, so the panel reads the
 * same whichever entity you opened, and a new renderer is a short file rather than a design task.
 *
 * Colour is never the only signal: every change carries an ADDED / MODIFIED / REMOVED chip, and
 * before/after blocks carry a screen-reader label. They deliberately do NOT carry a visible `−`/`+`
 * marker — many audited values are signed quantities (a −2 °C setpoint, a negative offset), and a
 * marker glued to the front of one reads as part of the number. The panel's legend explains the
 * red/green tinting instead.
 */

import React, { useState } from 'react'

import { FiChevronDown, FiChevronRight } from 'react-icons/fi'

import type { ChangeState, NormalizedChange } from './diff/normalize'
import { formatValue } from './diff/valueFormatters'

// ---- State chip ------------------------------------------------------------

/**
 * The chip colours, kept to the two the panel's legend defines.
 *
 * Red and green mean "previous value" and "new value" there, and an added row is all new value
 * while a removed row is all previous value, so those two chips can carry the legend's colours
 * honestly. A modified row is both at once — its own before/after blocks already say so in exactly
 * those colours — and tinting its chip a third colour introduced a hue the legend never explains,
 * which readers reasonably took for a further meaning. It stays neutral; the word does the work.
 */
const STATE_CHIP: Record<ChangeState, string> = {
  ADDED: 'bg-success/15 text-success ring-1 ring-success/40',
  MODIFIED: 'bg-base-200 text-base-content/70 ring-1 ring-base-300',
  REMOVED: 'bg-error/15 text-error ring-1 ring-error/40',
}

export const StateChip: React.FC<{ state: ChangeState }> = ({ state }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-field px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATE_CHIP[state]}`}
  >
    {state}
  </span>
)

// ---- Value block -----------------------------------------------------------

interface ValueBlockProps {
  tone: 'before' | 'after'
  value: unknown
  strike?: boolean
}

/**
 * One side of a change. Structured values collapse behind a disclosure so a 100-point polygon
 * cannot flood the panel.
 */
export const ValueBlock: React.FC<ValueBlockProps> = ({ tone, value, strike }) => {
  const [open, setOpen] = useState(false)
  const formatted = formatValue(value)

  const cls =
    tone === 'before' ? 'border-error/40 bg-error/10 text-error' : 'border-success/40 bg-success/10 text-success'

  return (
    <span className="inline-flex min-w-0 flex-col gap-1">
      <span className={`inline-flex min-w-0 items-center gap-1 rounded-field border px-2 py-1 text-sm ${cls}`}>
        <span className="sr-only">{tone === 'before' ? 'Previous value:' : 'New value:'}</span>
        <span className={`min-w-0 truncate ${strike ? 'line-through' : ''}`} title={formatted.text}>
          {formatted.text}
        </span>
        {formatted.expandable && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="shrink-0 opacity-70 hover:opacity-100"
            aria-label={open ? 'Hide value' : 'Show value'}
          >
            {open ? <FiChevronDown /> : <FiChevronRight />}
          </button>
        )}
      </span>
      {open && formatted.raw && (
        <pre className="max-h-48 overflow-auto rounded-field bg-base-200 p-2 text-xs text-base-content">
          {formatted.raw}
        </pre>
      )}
    </span>
  )
}

// ---- Change row (the atom) -------------------------------------------------

/**
 * The atomic before/after row. Every renderer that does not have a purpose-built layout for a
 * change falls back to this, so nothing is ever dropped silently.
 */
export const ChangeRow: React.FC<{ change: NormalizedChange }> = ({ change }) => (
  <div className="rounded-box border border-base-300 p-3">
    <StateChip state={change.state} />
    <p className="mt-2 text-sm font-medium" title={change.path}>
      {change.label}
    </p>
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {change.state !== 'ADDED' && (
        <ValueBlock tone="before" value={change.before} strike={change.state === 'REMOVED'} />
      )}
      {change.state === 'MODIFIED' && (
        <span aria-hidden className="text-base-content/50">
          →
        </span>
      )}
      {change.state !== 'REMOVED' && <ValueBlock tone="after" value={change.after} />}
    </div>
  </div>
)

export const ChangeList: React.FC<{ changes: NormalizedChange[] }> = ({ changes }) => (
  <div className="flex flex-col gap-3">
    {changes.map((change) => (
      <ChangeRow key={change.key} change={change} />
    ))}
  </div>
)

// ---- Section ---------------------------------------------------------------

interface SectionProps {
  title: string
  count?: number
  /** Collapsed by default for sections that can be large, such as geofence points. */
  defaultOpen?: boolean
  tone?: 'default' | 'success' | 'error'
  children: React.ReactNode
}

/** A collapsible group header with a count badge. */
export const Section: React.FC<SectionProps> = ({ title, count, defaultOpen = true, tone = 'default', children }) => {
  const [open, setOpen] = useState(defaultOpen)

  const headerTone =
    tone === 'success'
      ? 'bg-success/15 text-base-content'
      : tone === 'error'
        ? 'bg-error/15 text-base-content'
        : 'bg-base-200 text-base-content'

  return (
    <section className="overflow-hidden rounded-box border border-base-300">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${headerTone}`}
      >
        <span aria-hidden className="text-base">
          {open ? <FiChevronDown /> : <FiChevronRight />}
        </span>
        <span className="flex-1 truncate">{title}</span>
        {count !== undefined && (
          <span className="rounded-selector bg-base-300 px-1.5 py-0.5 text-[10px] font-semibold">{count}</span>
        )}
      </button>
      {open && <div className="p-3">{children}</div>}
    </section>
  )
}

// ---- Field table -----------------------------------------------------------

export interface FieldRow {
  label: string
  value: unknown
  /** Optional raw identifier shown on hover, e.g. the id behind a resolved name. */
  title?: string
}

/**
 * A two-column read-only list of resulting values.
 *
 * Used by the create and delete cards, where there is no meaningful "before" or "after" and a
 * before/after layout would be actively misleading.
 */
export const FieldTable: React.FC<{ rows: FieldRow[] }> = ({ rows }) => (
  <dl className="divide-y divide-base-200">
    {rows.map((row, index) => {
      const formatted = formatValue(row.value)
      return (
        <div key={`${row.label}-${index}`} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 py-1.5">
          <dt className="truncate text-sm text-base-content/60" title={row.title ?? row.label}>
            {row.label}
          </dt>
          <dd className="min-w-0 truncate text-sm" title={formatted.raw ?? formatted.text}>
            {formatted.text}
          </dd>
        </div>
      )
    })}
  </dl>
)

// ---- Empty state -----------------------------------------------------------

/**
 * What a record with no usable field-level changes renders as.
 *
 * A diff can be malformed, empty, or a shape we do not understand. None of those may produce an
 * empty panel: the reader still needs to know the change happened (staff can still copy the raw
 * payload from the panel footer). Handled centrally by the dispatcher so no individual renderer has to remember.
 */
export const NoChangesPanel: React.FC = () => (
  <div className="flex flex-col gap-3">
    <NoChanges />
  </div>
)

export const NoChanges: React.FC<{ message?: string }> = ({ message = 'No field-level changes were recorded.' }) => (
  <p className="rounded-box border border-base-300 bg-base-200 px-3 py-4 text-center text-sm text-base-content/60">
    {message}
  </p>
)

/**
 * Stands in for a panel while the names its ids resolve to are still loading.
 *
 * Without it the panel renders every reference as its raw id and then swaps each one for a name a
 * moment later, which reads as the record changing under the reader. A lookup that fails or finds
 * nothing still ends in the id fallback; this only covers the wait.
 */
export const LookupLoading: React.FC = () => (
  <div className="flex flex-col gap-3" role="status" aria-label="Loading names">
    <div className="skeleton h-5 w-1/3" />
    <div className="skeleton h-16 w-full" />
    <div className="skeleton h-24 w-full" />
  </div>
)
